import React, { useState } from 'react';
import styled from 'styled-components';

import {
  useFloating,
  shift,
  offset,
  useHover,
  autoPlacement,
  useFocus,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';

import { purify } from '../../utils/helpers';

import {
  Tooltip as EncoreTooltip,
  screenXsMin,
  spacer4,
  opacityWhite50,
  white,
  black,
  spacer24,
} from '@spotify-internal/encore-web';

const hashCode = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }
  return new Uint32Array([hash])[0].toString(8);
};
const TooltipTrigger = styled.span`
  display: inline-block;
  background-color: inherit;
  text-decoration: underline dashed ${opacityWhite50} 1px;
  text-underline-offset: ${spacer4};

  color: var(--text-base, ${white});
  font-size: var(--tooltip-content-font-size, 14px);
  line-height: ${spacer24};

  // provided by the theme
  text-decoration-color: var(--text-subdued, inherit);
`;

const TooltipContent = styled(EncoreTooltip)<{ isOpen: boolean }>`
  @media (min-width: 481px) {
    max-width: 280px;
  }
  @media (max-width: ${screenXsMin}) {
    max-width: calc(230px - 11vw);
  }
  @media (max-width: 370px) {
    max-width: calc(200px - 11vw);
  }
  width: max-content;
  /**
   * by containing layout we can prevent the arrow
   * from being affected by the tooltip box shadow
   */
  contain: layout;
  z-index: 100;
  background: ${white};
  color: var(--inverted-text-base, ${black});
`;

const BenefitsComparisonTooltip = ({
  content,
  children,
}: {
  content: string;
  children: any;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // generate a unique ID by associating content & children to be used by a11y
  const id = `tooltip-${hashCode(content + children)}`;

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(20),
      autoPlacement({
        allowedPlacements: ['left-end', 'right-end'],
        autoAlignment: true,
        padding: 6,
      }),
      shift(),
    ],
  });

  /**
   * a11y: the tooltip appears on hover & focus,
   * it can be dismissed via exit keyboard button
   */
  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
  ]);

  return (
    <>
      <TooltipTrigger
        ref={refs.setReference}
        tabIndex={0}
        role="tooltip"
        aria-describedby={id}
        {...getReferenceProps()}
      >
        {children}
      </TooltipTrigger>
      {isOpen && (
        <TooltipContent
          isOpen={isOpen}
          aria-hidden={!isOpen}
          id={id}
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          {...getFloatingProps()}
        >
          <span dangerouslySetInnerHTML={{ __html: purify(content) }} />
        </TooltipContent>
      )}
    </>
  );
};

export default BenefitsComparisonTooltip;
